import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { markPageUnloading } from "@/services/api";
import type { AuthUser, UserRole } from "@/types";

interface AuthState {
  role: UserRole;
  isAuthenticated: boolean;
  token: string | null;
  user: AuthUser | null;
  login: (role: UserRole, token: string, user: AuthUser) => void;
  logout: () => void;
}

const sessionAwareStorage = createJSONStorage(() => ({
  getItem: (key: string) => sessionStorage.getItem(key) ?? localStorage.getItem(key),
  setItem: (key: string, value: string) => {
    if (sessionStorage.getItem("_imp")) {
      sessionStorage.setItem(key, value);
    } else {
      localStorage.setItem(key, value);
    }
  },
  removeItem: (key: string) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  },
}));

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      role: "guest",
      isAuthenticated: false,
      token: null,
      user: null,
      login: (role, token, user) => set({ role, isAuthenticated: true, token, user }),
      logout: () => {
        set({ role: "guest", isAuthenticated: false, token: null, user: null });
        // Le device_id LiveKit identifie le **navigateur**, pas l'utilisateur : il doit survivre
        // au logout. Sans ça, chaque 401 (qui déclenche ce logout automatiquement, cf. api.ts)
        // réattribue une identité LiveKit au prof/élève à la reconnexion — LiveKit ne peut plus
        // remplacer l'ancienne connexion, laisse une session fantôme, et les commandes ciblent
        // la mauvaise connexion. C'est aussi ce qui rendait le suivi d'une enseignante
        // impossible dans la console LiveKit (une identité différente à chaque reconnexion).
        let deviceId: string | null = null;
        try {
          deviceId = localStorage.getItem(STORAGE_KEYS.LIVEKIT_DEVICE_ID);
        } catch {
          // stockage indisponible : rien à préserver
        }
        localStorage.clear();
        sessionStorage.clear();
        if (deviceId) {
          try {
            localStorage.setItem(STORAGE_KEYS.LIVEKIT_DEVICE_ID, deviceId);
          } catch {
            // best-effort : un échec de restauration ne doit pas bloquer la déconnexion
          }
        }
        // La redirection annule tout ce qui est encore en vol : ces échecs sont provoqués par la
        // déconnexion, pas subis. Marqué ici plutôt que chez les appelants — `logout()` navigue
        // lui-même, donc un marquage en amont arriverait après le départ (cf. le chemin 401).
        markPageUnloading();
        window.location.href = "/login";
      },
    }),
    { name: "auth-storage", storage: sessionAwareStorage },
  ),
);
